import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Link2,
  Plus,
  X,
  Search,
  Key,
  Server,
  FileText,
  MapPin,
  Calendar,
  Loader2,
  Trash2,
  ExternalLink,
  Package,
} from 'lucide-react';
import { documentationAPI } from '../../services/api';
import { useAuthStore } from '../../context/authStore';
import { isInternalRole } from '../../utils/roles.js';

// Icon mapping for asset types
const ASSET_ICONS = {
  password: Key,
  configuration: Server,
  flexible_asset: Package,
  article: FileText,
  location: MapPin,
  expiration: Calendar,
};

// Type display names
const TYPE_NAMES = {
  password: 'Password',
  configuration: 'Configuration',
  flexible_asset: 'Flexible Asset',
  article: 'Article',
  location: 'Location',
  expiration: 'Expiration',
};

// Get link for related asset
function getAssetLink(type, id, asset) {
  switch (type) {
    case 'password':
      return `/vault/${id}`;
    case 'configuration':
      return `/documentation/configurations/${id}`;
    case 'flexible_asset':
      return `/documentation/flexible-assets`;
    case 'article':
      return asset?.slug ? `/documentation/articles/${asset.slug}` : `/documentation/articles/${id}`;
    case 'location':
      return `/documentation/configurations?location=${id}`;
    case 'expiration':
      return `/documentation/expirations`;
    default:
      return '#';
  }
}

/**
 * Related Items Component
 * Displays and manages relationships between assets (like ITGlue)
 */
export default function RelatedItems({ sourceType, sourceId, companyId }) {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const canEdit = isInternalRole(user?.role);

  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Fetch relations for this asset
  const { data, isLoading, error } = useQuery({
    queryKey: ['asset-relations', sourceType, sourceId],
    queryFn: async () => {
      const response = await documentationAPI.getRelationsFor(sourceType, sourceId, { companyId });
      return response.data.relations || [];
    },
    enabled: !!sourceType && !!sourceId,
  });

  const relations = data || [];

  // Create relation mutation
  const createMutation = useMutation({
    mutationFn: (targetAsset) =>
      documentationAPI.createRelation({
        sourceType,
        sourceId: String(sourceId),
        targetType: targetAsset.type,
        targetId: String(targetAsset.id),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-relations', sourceType, sourceId] });
      setShowAddModal(false);
      setSearchQuery('');
      setSearchResults([]);
    },
  });

  // Delete relation mutation
  const deleteMutation = useMutation({
    mutationFn: (relationId) => documentationAPI.deleteRelation(relationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-relations', sourceType, sourceId] });
    },
  });

  // Search for relatable assets
  const handleSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await documentationAPI.searchRelatable(query, sourceType, sourceId, { companyId });
      setSearchResults(response.data.results || []);
    } catch (err) {
      console.error('Search failed:', err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [sourceType, sourceId, companyId]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  // Group relations by type
  const groupedRelations = relations.reduce((acc, rel) => {
    const type = rel.relatedType;
    if (!acc[type]) acc[type] = [];
    acc[type].push(rel);
    return acc;
  }, {});

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
        <div className="flex items-center gap-2 text-neutral-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading related items...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
        <p className="text-red-600 dark:text-red-400">Failed to load related items</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
        <div className="flex items-center gap-2">
          <Link2 className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
          <h3 className="text-lg font-medium text-neutral-900 dark:text-white">
            Related Items
          </h3>
          {relations.length > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 rounded-full">
              {relations.length}
            </span>
          )}
        </div>
        {canEdit && (
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Relation
          </button>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        {relations.length === 0 ? (
          <div className="text-center py-8">
            <Link2 className="w-12 h-12 mx-auto text-neutral-300 dark:text-neutral-600 mb-3" />
            <p className="text-neutral-500 dark:text-neutral-400">No related items</p>
            {canEdit && (
              <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-1">
                Click "Add Relation" to link related assets
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedRelations).map(([type, items]) => {
              const Icon = ASSET_ICONS[type] || Package;
              return (
                <div key={type}>
                  <h4 className="text-sm font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Icon className="w-4 h-4" />
                    {TYPE_NAMES[type] || type}s ({items.length})
                  </h4>
                  <div className="space-y-2">
                    {items.map((rel) => {
                      const asset = rel.relatedAsset;
                      const AssetIcon = ASSET_ICONS[rel.relatedType] || Package;
                      return (
                        <div
                          key={rel.id}
                          className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg group"
                        >
                          <Link
                            to={getAssetLink(rel.relatedType, rel.relatedId, asset)}
                            className="flex items-center gap-3 flex-1 min-w-0"
                          >
                            <div className="w-8 h-8 rounded-lg bg-white dark:bg-neutral-600 border border-neutral-200 dark:border-neutral-500 flex items-center justify-center flex-shrink-0">
                              <AssetIcon className="w-4 h-4 text-neutral-500 dark:text-neutral-300" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-neutral-900 dark:text-white truncate">
                                {asset?.name || asset?.title || `${TYPE_NAMES[rel.relatedType]} ${rel.relatedId}`}
                              </p>
                              {asset?.type_name && (
                                <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                                  {asset.type_name}
                                </p>
                              )}
                              {asset?.username && (
                                <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                                  {asset.username}
                                </p>
                              )}
                            </div>
                            <ExternalLink className="w-4 h-4 text-neutral-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                          </Link>
                          {canEdit && (
                            <button
                              onClick={() => deleteMutation.mutate(rel.id)}
                              disabled={deleteMutation.isPending}
                              className="ml-2 p-1.5 text-neutral-400 hover:text-red-600 dark:hover:text-red-400 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Remove relation"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Relation Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
              <h3 className="text-lg font-medium text-neutral-900 dark:text-white">
                Add Related Item
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setSearchQuery('');
                  setSearchResults([]);
                }}
                className="p-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {/* Search Input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for passwords, configurations, articles..."
                  className="w-full pl-10 pr-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  autoFocus
                />
                {isSearching && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 animate-spin" />
                )}
              </div>

              {/* Search Results */}
              <div className="mt-4 max-h-80 overflow-y-auto">
                {searchQuery.length < 2 ? (
                  <p className="text-center text-neutral-500 dark:text-neutral-400 py-8">
                    Type at least 2 characters to search
                  </p>
                ) : searchResults.length === 0 && !isSearching ? (
                  <p className="text-center text-neutral-500 dark:text-neutral-400 py-8">
                    No results found
                  </p>
                ) : (
                  <div className="space-y-2">
                    {searchResults.map((result) => {
                      const Icon = ASSET_ICONS[result.type] || Package;
                      const isAlreadyLinked = relations.some(
                        r => r.relatedType === result.type && r.relatedId === result.id
                      );

                      return (
                        <button
                          key={`${result.type}-${result.id}`}
                          onClick={() => !isAlreadyLinked && createMutation.mutate(result)}
                          disabled={isAlreadyLinked || createMutation.isPending}
                          className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                            isAlreadyLinked
                              ? 'bg-neutral-100 dark:bg-neutral-700/30 opacity-50 cursor-not-allowed'
                              : 'hover:bg-neutral-100 dark:hover:bg-neutral-700'
                          }`}
                        >
                          <div className="w-8 h-8 rounded-lg bg-neutral-100 dark:bg-neutral-600 flex items-center justify-center flex-shrink-0">
                            <Icon className="w-4 h-4 text-neutral-500 dark:text-neutral-300" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-neutral-900 dark:text-white truncate">
                              {result.name || result.title}
                            </p>
                            <p className="text-xs text-neutral-500 dark:text-neutral-400">
                              {TYPE_NAMES[result.type] || result.type}
                              {result.type_name && ` - ${result.type_name}`}
                              {result.username && ` (${result.username})`}
                            </p>
                          </div>
                          {isAlreadyLinked && (
                            <span className="text-xs text-neutral-500 dark:text-neutral-400">
                              Already linked
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
